import { useState, useEffect, useRef, useCallback } from 'react';
import { marked } from 'marked';
import './ExpandableAssistantLion.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAngleUp,
  faCircleArrowUp,
  faUser,
  faTrash,
  faStar,
  faThumbsUp,
  faThumbsDown,
  faFileWord,
  faCircle,
  faMicrophone,
  faXmark,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import './translations';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { getAuthHeaders, appendTokenToUrl, API_BASE_URL } from './auth';

function ExpandableAssistantLion({ userName, title, icon, expanded, sessionId, language, resetSessionId, chatPrefill, onCloseChat, onOpenChat, onResetMessages }) {
  const [vips, setVips] = useState([]);
  const [refreshCount, setRefreshCount] = useState(0);
  const [scrollBottom, setScrollBottom] = useState(0);
  const [events, setEvents] = useState([]);
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState('chat');
  const [messages, setMessages] = useState([{ text: t("chat_welcome_message"), sender: 'bot', isHtml: true }]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const chatContentRef = useRef(null);
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const textAreaRef = useRef(null);
  const currentBotMessage = useRef('');
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const audioContextRef = useRef(null);
  const mediaStreamSourceRef = useRef(null);
  const processorRef = useRef(null);
  const audioBufferRef = useRef([]);

  // Voice mode states (from ChatModal)
  const [voiceState, setVoiceState] = useState('idle'); // 'idle' | 'initializing' | 'listening' | 'thinking' | 'speaking'
  const mediaStreamRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const ttsAudioRef = useRef(null);
  const ttsAudioContextRef = useRef(null);
  const vadFrameIdRef = useRef(null);
  const speechStartTimeRef = useRef(null);
  const silenceStartTimeRef = useRef(null);
  const voiceStoppedByUserRef = useRef(false);

  // VAD settings
  const VAD_THRESHOLD = 0.025;
  const MIN_SPEECH_MS = 400;
  const SILENCE_MS = 1000;

  const [audioLevel, setAudioLevel] = useState(0);
  const [processingPulse, setProcessingPulse] = useState(0);

  // Processing sound settings
  const PROCESSING_SOUND_VOLUME = 0.25;
  const PROCESSING_SOUND_SPEED = 320;

  // Processing sound refs
  const processingAudioContextRef = useRef(null);
  const processingOscillatorRef = useRef(null);
  const processingGainRef = useRef(null);
  const processingIntervalRef = useRef(null);

  // Audio queue for streaming TTS playback
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const currentTranscriptRef = useRef('');
  const botResponseRef = useRef('');
  const streamDoneRef = useRef(false);
  const startListeningRef = useRef(null);
  const abortControllerRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);
  const MAX_CONSECUTIVE_FAILURES = 3;

  // Stop processing sound
  const stopProcessingSound = useCallback(() => {
    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current);
      processingIntervalRef.current = null;
    }
    if (processingOscillatorRef.current) {
      try {
        processingOscillatorRef.current.stop();
      } catch (e) {}
      processingOscillatorRef.current = null;
    }
    if (processingGainRef.current && processingAudioContextRef.current) {
      try {
        const ctx = processingAudioContextRef.current;
        const gain = processingGainRef.current;
        gain.gain.cancelScheduledValues(ctx.currentTime);
        gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
        setTimeout(() => {
          if (processingAudioContextRef.current) {
            try {
              processingAudioContextRef.current.close();
            } catch (e) {}
            processingAudioContextRef.current = null;
          }
        }, 150);
      } catch (e) {
        if (processingAudioContextRef.current) {
          try {
            processingAudioContextRef.current.close();
          } catch (e) {}
          processingAudioContextRef.current = null;
        }
      }
      processingGainRef.current = null;
    } else if (processingAudioContextRef.current) {
      try {
        processingAudioContextRef.current.close();
      } catch (e) {}
      processingAudioContextRef.current = null;
    }
  }, []);

  // Start processing sound - Neural Pulse (deep processing pulses)
  const startProcessingSound = useCallback(() => {
    try {
      stopProcessingSound();

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      processingAudioContextRef.current = audioCtx;

      const gainNode = audioCtx.createGain();
      gainNode.connect(audioCtx.destination);
      processingGainRef.current = gainNode;

      const baseFreq = 140;
      let step = 0;
      const pattern = [0, 20, 40, 20, 0, -20, -40, -20];

      processingIntervalRef.current = setInterval(() => {
        if (!processingGainRef.current || !processingAudioContextRef.current) return;
        const ctx = processingAudioContextRef.current;
        const gain = processingGainRef.current;

        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = baseFreq + pattern[step % pattern.length];
        osc.connect(gain);

        gain.gain.cancelScheduledValues(ctx.currentTime);
        gain.gain.setValueAtTime(PROCESSING_SOUND_VOLUME, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);

        osc.start();
        osc.stop(ctx.currentTime + 0.14);

        setProcessingPulse(step % pattern.length);
        step++;
      }, PROCESSING_SOUND_SPEED);

    } catch (e) {
      console.warn('Could not start processing sound:', e);
    }
  }, [stopProcessingSound]);

  // Start/stop processing sound based on voice state
  useEffect(() => {
    if (voiceState === 'thinking') {
      startProcessingSound();
    } else {
      stopProcessingSound();
    }
    return () => stopProcessingSound();
  }, [voiceState, startProcessingSound, stopProcessingSound]);

  const computeRMS = useCallback((analyser) => {
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const val = (data[i] - 128) / 128;
      sum += val * val;
    }
    return Math.sqrt(sum / data.length);
  }, []);

  const playNextInQueue = useCallback(() => {
    console.log('🎵 playNextInQueue called, queue length:', audioQueueRef.current.length, 'streamDone:', streamDoneRef.current);

    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;

      if (streamDoneRef.current) {
        console.log('🔄 Queue empty & stream done, transitioning...');
        if (botResponseRef.current) {
          setMessages(m => [...m, { text: botResponseRef.current, sender: 'bot' }]);
          botResponseRef.current = '';
        }
        if (!voiceStoppedByUserRef.current && startListeningRef.current) {
          console.log('🎤 Restarting listening...');
          startListeningRef.current();
        } else if (voiceStoppedByUserRef.current) {
          console.log('🛑 User stopped voice mode, not restarting listening');
        }
      }
      return;
    }

    isPlayingRef.current = true;
    setVoiceState('speaking');

    const nextAudio = audioQueueRef.current.shift();
    console.log('🔊 Playing audio chunk', nextAudio.index);

    const playWithWebAudio = async () => {
      try {
        if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
          ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        const audioContext = ttsAudioContextRef.current;

        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        const binaryString = atob(nextAudio.audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const audioBuffer = await audioContext.decodeAudioData(bytes.buffer.slice(0));

        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);

        source.onended = () => {
          console.log('✅ Audio chunk ended (Web Audio)');
          ttsAudioRef.current = null;
          playNextInQueue();
        };

        source.start(0);

        ttsAudioRef.current = { source, context: audioContext };

      } catch (e) {
        console.error('Web Audio playback failed, falling back to Audio element:', e);
        const audioSrc = `data:audio/mp3;base64,${nextAudio.audio}`;
        const audio = new Audio(audioSrc);
        ttsAudioRef.current = audio;

        audio.onended = () => {
          console.log('✅ Audio chunk ended (fallback)');
          ttsAudioRef.current = null;
          playNextInQueue();
        };

        audio.onerror = (err) => {
          console.error('❌ Audio error (fallback):', err);
          ttsAudioRef.current = null;
          playNextInQueue();
        };

        audio.play().catch((err) => {
          console.error('❌ Audio play failed (fallback):', err);
          ttsAudioRef.current = null;
          playNextInQueue();
        });
      }
    };

    playWithWebAudio();
  }, []);

  // Helper to detect supported audio MIME type for MediaRecorder
  const getSupportedMimeType = useCallback(() => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/wav',
      ''
    ];

    for (const type of types) {
      if (type === '' || MediaRecorder.isTypeSupported(type)) {
        console.log(`🎤 Using MIME type: ${type || 'browser default'}`);
        return type;
      }
    }
    return '';
  }, []);

  const sendVoiceToBackend = useCallback(async (audioBlob) => {
    setVoiceState('thinking');
    setLoading(true);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    audioQueueRef.current = [];
    isPlayingRef.current = false;
    currentTranscriptRef.current = '';
    botResponseRef.current = '';
    streamDoneRef.current = false;

    const mimeToExt = {
      'audio/webm': 'webm',
      'audio/webm;codecs=opus': 'webm',
      'audio/mp4': 'mp4',
      'audio/ogg': 'ogg',
      'audio/ogg;codecs=opus': 'ogg',
      'audio/wav': 'wav',
    };
    const ext = mimeToExt[audioBlob.type] || 'webm';

    const formData = new FormData();
    formData.append('audio', audioBlob, `recording.${ext}`);
    formData.append('session_id', sessionId);
    formData.append('language', language.language);

    try {
      // TODO: Replace with actual voice endpoint
      const res = await fetch(`${API_BASE_URL}/api/voice_message`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
        signal: abortController.signal
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        if (abortController.signal.aborted) {
          console.log('🛑 Request aborted, stopping stream processing');
          reader.cancel();
          break;
        }

        const { done, value } = await reader.read();
        if (done) {
          // Flush the TextDecoder and process any remaining buffered data
          buffer += decoder.decode();
          if (buffer.trim()) {
            const remainingEvents = buffer.split('\n\n');
            for (const event of remainingEvents) {
              if (!event.trim()) continue;
              let eventType = '';
              let eventData = '';
              for (const line of event.split('\n')) {
                if (line.startsWith('event: ')) eventType = line.slice(7).trim();
                else if (line.startsWith('data: ')) eventData = line.slice(6);
              }
              if (!eventType || !eventData) continue;
              try {
                const data = JSON.parse(eventData);
                console.log(`📨 SSE Event (buffer flush): ${eventType}`, data.audio ? '(audio data)' : data);
                if (eventType === 'done') {
                  botResponseRef.current = data.full_response || '';
                  streamDoneRef.current = true;
                  if (audioQueueRef.current.length === 0 && !isPlayingRef.current && data.full_response) {
                    setMessages(m => [...m, { text: data.full_response, sender: 'bot' }]);
                    botResponseRef.current = '';
                  }
                }
              } catch (e) {
                console.warn('Failed to parse remaining SSE data:', eventData, e);
              }
            }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          if (abortController.signal.aborted) break;

          if (!event.trim()) continue;

          let eventType = '';
          let eventData = '';

          for (const line of event.split('\n')) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            }
          }

          if (!eventType || !eventData) continue;

          try {
            const data = JSON.parse(eventData);
            console.log(`📨 SSE Event: ${eventType}`, data.audio ? '(audio data)' : data);

            if (eventType === 'transcript' && data.text) {
              currentTranscriptRef.current = data.text;
              setMessages(m => [...m, { text: data.text, sender: 'user' }]);
              consecutiveFailuresRef.current = 0;
            }
            else if (eventType === 'text' && data.chunk) {
              botResponseRef.current += data.chunk;
            }
            else if (eventType === 'audio' && data.audio) {
              console.log(`🔊 Audio chunk ${data.index} received, queue length: ${audioQueueRef.current.length}`);
              audioQueueRef.current.push(data);

              if (!isPlayingRef.current) {
                console.log('▶️ Starting playback');
                playNextInQueue();
              }
            }
            else if (eventType === 'done') {
              console.log('✅ Stream done, queue length:', audioQueueRef.current.length, 'isPlaying:', isPlayingRef.current);
              botResponseRef.current = data.full_response || '';
              streamDoneRef.current = true;

              if (data.reason === 'empty_transcript' || (!data.full_response && audioQueueRef.current.length === 0)) {
                consecutiveFailuresRef.current++;
                console.log(`📭 Empty response (failure ${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES})`);
                setLoading(false);

                if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                  console.log('❌ Too many consecutive failures, stopping voice mode');
                  consecutiveFailuresRef.current = 0;
                  if (mediaStreamRef.current) {
                    mediaStreamRef.current.getTracks().forEach(t => t.stop());
                    mediaStreamRef.current = null;
                  }
                  if (audioContextRef.current) {
                    audioContextRef.current.close().catch(() => {});
                    audioContextRef.current = null;
                  }
                  setVoiceState('idle');
                  return;
                }

                if (startListeningRef.current && !voiceStoppedByUserRef.current) {
                  if (mediaStreamRef.current) {
                    mediaStreamRef.current.getTracks().forEach(t => t.stop());
                    mediaStreamRef.current = null;
                  }
                  if (audioContextRef.current) {
                    audioContextRef.current.close().catch(() => {});
                    audioContextRef.current = null;
                  }
                  setTimeout(() => {
                    if (!voiceStoppedByUserRef.current) {
                      startListeningRef.current();
                    }
                  }, 500);
                }
                return;
              }

              consecutiveFailuresRef.current = 0;

              if (audioQueueRef.current.length === 0 && !isPlayingRef.current) {
                if (data.full_response) {
                  setMessages(m => [...m, { text: data.full_response, sender: 'bot' }]);
                }
                botResponseRef.current = '';
                if (startListeningRef.current) {
                  startListeningRef.current();
                }
              }
            }
            else if (eventType === 'error') {
              console.error('Voice stream error:', data.message);
              setMessages(m => [...m, { text: 'Voice message failed. Please try again.', sender: 'bot' }]);
              setVoiceState('idle');
            }
          } catch (e) {
            console.warn('Failed to parse SSE data:', eventData, e);
          }
        }
      }
      // Safety net: if stream ended but done event was never processed, add the bot message
      if (!streamDoneRef.current && botResponseRef.current && botResponseRef.current.trim()) {
        console.log('⚠️ Safety net: adding bot response that was never confirmed by done event');
        setMessages(m => [...m, { text: botResponseRef.current, sender: 'bot' }]);
        botResponseRef.current = '';
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('🛑 Request was aborted (barge-in)');
        if (botResponseRef.current && botResponseRef.current.trim()) {
          const partialResponse = botResponseRef.current;
          setMessages(m => [...m, { text: partialResponse, sender: 'bot' }]);
          console.log('📝 Saved partial response:', partialResponse.substring(0, 50) + '...');
        }
        botResponseRef.current = '';
        return;
      }
      console.error('Voice request failed:', err);

      consecutiveFailuresRef.current++;
      console.log(`⚠️ Error occurred (failure ${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES})`);

      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
        console.log('❌ Too many consecutive failures, stopping voice mode');
        consecutiveFailuresRef.current = 0;
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(t => t.stop());
          mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
        setVoiceState('idle');
        return;
      }

      if (startListeningRef.current && !voiceStoppedByUserRef.current) {
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(t => t.stop());
          mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
        setTimeout(() => {
          if (!voiceStoppedByUserRef.current) {
            startListeningRef.current();
          }
        }, 500);
      } else {
        setVoiceState('idle');
      }
    } finally {
      setLoading(false);
      if (abortControllerRef.current?.signal.aborted === false) {
        abortControllerRef.current = null;
      }
    }
  }, [sessionId, language, playNextInQueue]);

  const startListening = useCallback(async () => {
    try {
      let stream = mediaStreamRef.current;
      let audioCtx = audioContextRef.current;
      let analyser = analyserRef.current;

      if (!stream || stream.getTracks().every(t => t.readyState === 'ended')) {
        console.log('🎤 Creating new media stream');
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;

        if (audioCtx) {
          audioCtx.close().catch(() => {});
        }
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;

        const source = audioCtx.createMediaStreamSource(stream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        analyserRef.current = analyser;
      } else {
        console.log('🎤 Reusing existing media stream');
        if (audioCtx && audioCtx.state === 'suspended') {
          await audioCtx.resume();
        }
      }

      const mimeType = getSupportedMimeType();
      const recorderOptions = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        if (voiceStoppedByUserRef.current) {
          console.log('🛑 Recorder stopped but user closed voice mode, discarding audio');
          audioChunksRef.current = [];
          return;
        }

        if (audioChunksRef.current.length > 0) {
          const actualMimeType = recorder.mimeType || 'audio/webm';
          const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
          audioChunksRef.current = [];

          const speechDuration = speechStartTimeRef.current
            ? Date.now() - speechStartTimeRef.current
            : 0;

          if (blob.size < 3000 || speechDuration < 500) {
            console.log(`⚠️ Audio rejected: size=${blob.size}, speechDuration=${speechDuration}ms`);
            if (startListeningRef.current && !voiceStoppedByUserRef.current) {
              startListeningRef.current();
            }
            return;
          }

          sendVoiceToBackend(blob);
        }
      };

      recorder.start(100);
      setVoiceState('listening');
      speechStartTimeRef.current = null;
      silenceStartTimeRef.current = null;

      const vadLoop = () => {
        if (!analyserRef.current) return;

        const rms = computeRMS(analyserRef.current);
        setAudioLevel(Math.min(rms * 10, 1));
        const now = Date.now();

        if (rms > VAD_THRESHOLD) {
          silenceStartTimeRef.current = null;
          if (!speechStartTimeRef.current) {
            speechStartTimeRef.current = now;
          }
        } else {
          if (speechStartTimeRef.current && (now - speechStartTimeRef.current) >= MIN_SPEECH_MS) {
            if (!silenceStartTimeRef.current) {
              silenceStartTimeRef.current = now;
            } else if ((now - silenceStartTimeRef.current) >= SILENCE_MS) {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
              }
              return;
            }
          }
        }

        vadFrameIdRef.current = requestAnimationFrame(vadLoop);
      };

      vadFrameIdRef.current = requestAnimationFrame(vadLoop);

    } catch (err) {
      console.error('Microphone access denied:', err);
      setVoiceState('idle');
    }
  }, [computeRMS, sendVoiceToBackend, getSupportedMimeType]);

  // Keep ref updated for playNextInQueue to call
  useEffect(() => {
    startListeningRef.current = startListening;
  }, [startListening]);

  // VAD loop for listening state
  useEffect(() => {
    if (voiceState !== 'listening' || !analyserRef.current || !mediaRecorderRef.current) {
      return;
    }

    if (mediaRecorderRef.current.state !== 'recording') {
      return;
    }

    console.log('🎧 Starting VAD loop for listening state');

    const vadLoop = () => {
      if (!analyserRef.current || voiceState !== 'listening') return;

      const rms = computeRMS(analyserRef.current);
      setAudioLevel(Math.min(rms * 10, 1));
      const now = Date.now();

      if (rms > VAD_THRESHOLD) {
        silenceStartTimeRef.current = null;
        if (!speechStartTimeRef.current) {
          speechStartTimeRef.current = now;
        }
      } else {
        if (speechStartTimeRef.current && (now - speechStartTimeRef.current) >= MIN_SPEECH_MS) {
          if (!silenceStartTimeRef.current) {
            silenceStartTimeRef.current = now;
          } else if ((now - silenceStartTimeRef.current) >= SILENCE_MS) {
            console.log('🔇 Silence detected, stopping recorder');
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
            }
            return;
          }
        }
      }

      vadFrameIdRef.current = requestAnimationFrame(vadLoop);
    };

    vadFrameIdRef.current = requestAnimationFrame(vadLoop);

    return () => {
      if (vadFrameIdRef.current) {
        cancelAnimationFrame(vadFrameIdRef.current);
        vadFrameIdRef.current = null;
      }
    };
  }, [voiceState, computeRMS]);

  const stopVoice = useCallback(() => {
    console.log('🛑 stopVoice called - user closing voice mode');
    voiceStoppedByUserRef.current = true;

    // Save any pending bot response before clearing state
    if (botResponseRef.current && botResponseRef.current.trim()) {
      setMessages(m => [...m, { text: botResponseRef.current, sender: 'bot' }]);
      botResponseRef.current = '';
    }

    stopProcessingSound();

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (vadFrameIdRef.current) {
      cancelAnimationFrame(vadFrameIdRef.current);
      vadFrameIdRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (ttsAudioRef.current) {
      if (ttsAudioRef.current.source) {
        try {
          ttsAudioRef.current.source.stop();
        } catch (e) {}
      } else if (ttsAudioRef.current.pause) {
        ttsAudioRef.current.pause();
      }
      ttsAudioRef.current = null;
    }
    if (ttsAudioContextRef.current) {
      ttsAudioContextRef.current.close().catch(() => {});
      ttsAudioContextRef.current = null;
    }
    if (audioQueueRef.current) {
      audioQueueRef.current = [];
    }
    if (isPlayingRef.current !== undefined) {
      isPlayingRef.current = false;
    }
    streamDoneRef.current = false;
    audioChunksRef.current = [];
    setAudioLevel(0);
    setVoiceState('idle');
  }, [stopProcessingSound]);

  // Stop and speak again (barge-in)
  const stopAndSpeakAgain = useCallback(() => {
    console.log('🔄 Stop and speak again - returning to listening mode');

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (ttsAudioRef.current) {
      if (ttsAudioRef.current.source) {
        try {
          ttsAudioRef.current.source.stop();
        } catch (e) {}
      } else if (ttsAudioRef.current.pause) {
        ttsAudioRef.current.pause();
      }
      ttsAudioRef.current = null;
    }

    audioQueueRef.current = [];
    isPlayingRef.current = false;
    streamDoneRef.current = false;

    speechStartTimeRef.current = null;
    silenceStartTimeRef.current = null;

    startListening();
  }, [startListening]);

  const toggleVoice = useCallback(async () => {
    if (voiceState === 'idle') {
      voiceStoppedByUserRef.current = false;
      setVoiceState('initializing');

      try {
        const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
        silentAudio.volume = 0;
        silentAudio.play().then(() => silentAudio.pause()).catch(() => {});
        console.log('🔓 Audio playback unlock initiated');

        if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
          ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
          console.log('🔓 TTS AudioContext created during user gesture');
        }
        if (ttsAudioContextRef.current.state === 'suspended') {
          ttsAudioContextRef.current.resume().catch(() => {});
        }

        console.log('🎤 Requesting microphone permission during user gesture...');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('🎤 Microphone permission granted!');
        mediaStreamRef.current = stream;

        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
        }
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;

        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        analyserRef.current = analyser;

        startListening();

      } catch (err) {
        console.error('Failed to initialize voice:', err);
        setVoiceState('idle');
      }
    } else {
      stopVoice();
    }
  }, [voiceState, startListening, stopVoice]);

  const modifyLinks = (htmlText) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');

    const links = doc.querySelectorAll('a');
    links.forEach(link => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });

    return doc.body.innerHTML;
  };

  const renderMessage = (text) => {
    const rawHtml = marked.parse(text);
    return modifyLinks(rawHtml);
  };

  const formatDateTime = (dateTimeStr) => {
    const dateTime = new Date(dateTimeStr);
    const date = dateTime.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const time = dateTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false });
    return { date, time };
  };

  const fetchEvents = useCallback(async () => {
    setEvents([]);
    setCalendarLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/fetch_calendar_events?date=${selectedDate}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setEvents(data.events);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setCalendarLoading(false);
    }
  }, [selectedDate]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleEventClick = (event) => {
    const { summary, description, start, end } = event;
    const { date, time: startTime } = formatDateTime(start.dateTime);
    const { time: endTime } = formatDateTime(end.dateTime);

    const apiMessage = `Ich habe folgenden Kalendereintrag. Erstelle mir nützliche Informationen, um für den Termin vorbereitet zu sein:
Titel: ${summary}
Beschreibung: ${description}
Datum: ${date}
Start: ${startTime}
Ende: ${endTime}`;

    setSelectedTab('chat');
    setNewMessage(apiMessage);

    setTimeout(() => {
      handleSendMessage();
    }, 0);
  };

  const toggle = () => {
    if (expanded && onCloseChat) {
      onCloseChat();
    } else if (!expanded && onOpenChat) {
      onOpenChat();
    }
  };

  const resetMessage = () => {
    setMessages([
      {
        text: t("chat_welcome_message"),
        sender: 'bot',
        isHtml: true
      }
    ]);
    setNewMessage('');
    resetSessionId();
    onResetMessages();
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
    }
  };

  useEffect(() => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
    }
  }, [messages, scrollBottom]);

  useEffect(() => {
    setMessages([
      {
        text: t("chat_welcome_message"),
        sender: 'bot',
        isHtml: true
      }
    ]);
  }, [t]);

  useEffect(() => {
    if (selectedTab === 'calendar' && selectedDate != null) {
      fetchEvents();
    }
  }, [selectedDate, selectedTab, fetchEvents]);

  useEffect(() => {
    const fetchVIPs = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/vip?user_name=${encodeURIComponent(userName)}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setVips(data);
      } catch (error) {
        console.error('Failed to fetch VIPs:', error);
      }
    };

    fetchVIPs();
  }, [refreshCount, userName]);

  useEffect(() => {
    if (expanded && chatPrefill) {
      setNewMessage(chatPrefill);
    }
  }, [expanded, chatPrefill]);

  const handleCreateVIP = async (message) => {
    const vipData = {
      user_name: userName,
      prompt: message.text
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/vip`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(vipData)
      });

      await response.json();
    } catch (error) {
      console.error('Error posting VIP:', error);
    }
    setRefreshCount((current) => current + 1);
  };

  const handleDeleteVip = async (vipId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/vip/${vipId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to delete VIP');
      }
      setVips((currentVips) => currentVips.filter((vip) => vip.id !== vipId));
    } catch (error) {
      console.error('Error deleting VIP:', error);
    }
    setRefreshCount((current) => current + 1);
  };

  const handleEventClickVip = (message) => {
    setSelectedTab('chat');
    setNewMessage(message);

    setTimeout(() => {
      const textarea = textAreaRef.current;
      if (textarea) {
        textarea.style.height = 'auto';
        let newHeight = textarea.scrollHeight;

        if (newHeight <= 28) {
          newHeight = 28;
          textarea.style.overflowY = 'hidden';
        } else if (newHeight > 150) {
          newHeight = 150;
          textarea.style.overflowY = 'auto';
        } else {
          textarea.style.overflowY = 'hidden';
        }
        textarea.style.height = newHeight + 'px';
      }
    }, 0);
    setScrollBottom((current) => current + 1);
  };

  const generateWordDocument = async (messages) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate_word_document`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ messages }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ChatHistory.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error generating document:', error);
    }
  };

  const handleInput = (e) => {
    setNewMessage(e.target.value);

    const textarea = textAreaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';

    let newHeight = textarea.scrollHeight;

    if (newHeight <= 28) {
      newHeight = 28;
      textarea.style.overflowY = 'hidden';
    } else if (newHeight > 150) {
      newHeight = 150;
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
    textarea.style.height = newHeight + 'px';
  };

  const handleSendMessage = async () => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
    }
    if (newMessage.trim()) {
      const userMessage = { text: newMessage, sender: 'user' };
      setMessages(prevMessages => [...prevMessages, userMessage]);
      const messageToSend = newMessage;
      setNewMessage('');
      setLoading(true);
      currentBotMessage.current = "";

      try {
        const eventSource = new EventSource(
          appendTokenToUrl(`/api/chat?user_input=${encodeURIComponent(messageToSend)}&session_id=${sessionId}&language=${language.language}`)
        );

        eventSource.onmessage = (event) => {
          const chunk = event.data;
          if (chunk !== 'end of response') {
            setLoading(false);
            const parsedChunk = chunk.replace(/<newline>/g, '\n');
            currentBotMessage.current += parsedChunk;
            setMessages(messages => {
              const lastMessage = messages[messages.length - 1];
              if (lastMessage && lastMessage.sender === 'bot') {
                return [...messages.slice(0, -1), { ...lastMessage, text: currentBotMessage.current }];
              } else {
                return [...messages, { text: currentBotMessage.current, sender: 'bot' }];
              }
            });
          } else {
            eventSource.close();
          }
        };

        eventSource.onerror = (error) => {
          console.error('Error fetching response:', error);
          setMessages(prevMessages => [
            ...prevMessages,
            { text: 'Failed to get response from server.', sender: 'bot' }
          ]);
          setLoading(false);
          eventSource.close();
        };

        eventSource.addEventListener('end', () => {
          setMessages(messages => {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage && lastMessage.sender === 'bot') {
              return [...messages.slice(0, -1), { text: currentBotMessage.current, sender: 'bot' }];
            } else {
              return [...messages, { text: currentBotMessage.current, sender: 'bot' }];
            }
          });
          setLoading(false);
          eventSource.close();
        });

      } catch (error) {
        console.error('Error fetching response:', error);
        setMessages(prevMessages => [
          ...prevMessages,
          { text: 'Failed to get response from server.', sender: 'bot' }
        ]);
        setLoading(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleRecordClick = async () => {
    if (isRecording) {
      if (processorRef.current && mediaStreamSourceRef.current) {
        mediaStreamSourceRef.current.disconnect();
        processorRef.current.disconnect();
      }

      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      if (mediaStreamSourceRef.current && mediaStreamSourceRef.current.mediaStream) {
        mediaStreamSourceRef.current.mediaStream.getTracks().forEach(track => track.stop());
      }

      setIsRecording(false);

      processAudioData(audioBufferRef.current);
    } else {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Your browser does not support audio recording.');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        mediaStreamSourceRef.current = audioContext.createMediaStreamSource(stream);
        processorRef.current = audioContext.createScriptProcessor(4096, 1, 1);

        mediaStreamSourceRef.current.connect(processorRef.current);
        processorRef.current.connect(audioContext.destination);

        audioBufferRef.current = [];

        processorRef.current.onaudioprocess = (e) => {
          const inputBuffer = e.inputBuffer.getChannelData(0);
          audioBufferRef.current.push(new Float32Array(inputBuffer));
        };

        setIsRecording(true);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        alert('Could not access your microphone. Please check permissions.');
      }
    }
  };

  const processAudioData = async (buffer) => {
    setIsAudioLoading(true);

    const wavBlob = encodeWAV(buffer);
    const reader = new FileReader();
    reader.readAsDataURL(wavBlob);

    reader.onloadend = async () => {
      const base64data = reader.result.split(',')[1];

      try {
        const response = await fetch(`${API_BASE_URL}/api/translate_audio_to_text`, {
          method: 'POST',
          headers: getAuthHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({ audio_file: base64data }),
        });

        if (!response.ok) {
          throw new Error('Failed to process audio');
        }

        const data = await response.json();
        if (data && data.user_input) {
          setNewMessage(data.user_input);
        } else {
          console.error('Invalid response from API');
        }
      } catch (error) {
        console.error('Error processing audio:', error);
      } finally {
        setIsAudioLoading(false);
      }
    };
  };

  const encodeWAV = (buffers) => {
    const sampleRate = audioContextRef.current.sampleRate;
    const bufferLength = buffers.length * buffers[0].length;
    const buffer = new ArrayBuffer(44 + bufferLength * 2);
    const view = new DataView(buffer);

    const writeString = (view, offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + bufferLength * 2, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, 'data');
    view.setUint32(40, bufferLength * 2, true);

    let offset = 44;
    buffers.forEach((buffer) => {
      for (let i = 0; i < buffer.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, buffer[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      }
    });

    return new Blob([view], { type: 'audio/wav' });
  };

  return (
    <div className="expandable-chat-container">
      <div className="expandable-header" onClick={toggle}>
        <span>
          <FontAwesomeIcon
            icon={icon}
            className='expandable-icon'
          />
          {` ${title}`}
        </span>
        <FontAwesomeIcon
          icon={expanded ? faAngleUp : faAngleDown}
          className="arrow"
        />
      </div>
      <div className={`tab-content ${expanded ? 'open' : ''}`}>
        {expanded && (
          <>
            <div className="chat-tabs-header">
              <button
                className={`chat-tab-button ${selectedTab === 'chat' ? 'active' : ''}`}
                onClick={() => setSelectedTab('chat')}
              >
                {t('chat-tab')}
              </button>
              <button
                className={`chat-tab-button ${selectedTab === 'calendar' ? 'active' : ''}`}
                onClick={() => setSelectedTab('calendar')}
              >
                {t('calendar-tab')}
              </button>
              <button
                className={`chat-tab-button ${selectedTab === 'vip' ? 'active' : ''}`}
                onClick={() => setSelectedTab('vip')}
              >
                {t('very-important-prompts')}
              </button>
            </div>
            <div className="tabs-content">
              {selectedTab === 'chat' && (
                <>
                  <div className={`chat-content-wrapper${voiceState !== 'idle' ? ' voice-active' : ''}`}>
                    <div className="chat-content" ref={chatContentRef}>
                      {messages.map((message, index) => (
                        <div
                          key={index}
                          className={`message-icon-container ${message.sender}`}
                        >
                          {message.sender === 'bot' && (
                            <div className="message-icon">
                              E
                            </div>
                          )}
                          {message.sender === 'user' && (
                            <div
                              className="star-icon"
                              onClick={() => handleCreateVIP(message)}
                            >
                              <FontAwesomeIcon icon={faStar} />
                            </div>
                          )}
                          <div className={`chat-message ${message.sender}`}>
                            <div className='markdown-container'>
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeRaw]}
                              >
                                {renderMessage(message.text)}
                              </ReactMarkdown>
                              {index !== 0 && message.sender === 'bot' && (
                                <div className='bot-response-reaction-container'>
                                  <div className='thumps-container'>
                                    <div className="thumps-icon">
                                      <FontAwesomeIcon icon={faThumbsUp} />
                                    </div>
                                    <div className="thumps-icon">
                                      <FontAwesomeIcon icon={faThumbsDown} />
                                    </div>
                                  </div>
                                  <div className="word-icon">
                                    <FontAwesomeIcon
                                      icon={faFileWord}
                                      onClick={() => generateWordDocument(messages.slice(1))}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          {message.sender === 'user' && (
                            <div className="user-icon">
                              <FontAwesomeIcon icon={faUser} />
                            </div>
                          )}
                        </div>
                      ))}
                      {loading && (
                        <div className="chat-bot-loader">
                          <div className="chat-loader"></div>
                          <span className="loading-text">
                            {t('load-response')}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Voice overlay */}
                    {voiceState !== 'idle' && (
                      <div
                        className="voice-overlay"
                        onClick={voiceState === 'thinking' || voiceState === 'speaking' ? stopAndSpeakAgain : undefined}
                        style={{ cursor: voiceState === 'listening' || voiceState === 'initializing' ? 'default' : 'pointer' }}
                      >
                        <div className="voice-overlay-bg" />
                        <div className="voice-status-dot" />

                        {voiceState === 'initializing' ? (
                          <div className="voice-initializing-spinner">
                            <FontAwesomeIcon icon={faSpinner} spin />
                          </div>
                        ) : (
                          <div className="voice-visualizer-container">
                            <div
                              className={`voice-blob ${voiceState === 'listening' ? 'voice-blob-listening' : ''} ${voiceState === 'thinking' ? 'voice-blob-thinking' : ''} ${voiceState === 'speaking' ? 'voice-blob-speaking' : ''}`}
                              style={{
                                transform: voiceState === 'thinking'
                                  ? `scale(${1 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.04})`
                                  : `scale(${1 + audioLevel * 0.3})`,
                                transition: 'transform 0.12s ease-out',
                              }}
                            >
                              <div className="voice-blob-inner" />
                              <div
                                className="voice-blob-ring voice-blob-ring-1"
                                style={voiceState === 'thinking' ? {
                                  transform: `scale(${1.1 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.05})`,
                                  opacity: 0.3 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.08,
                                  transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
                                } : {}}
                              />
                              <div
                                className="voice-blob-ring voice-blob-ring-2"
                                style={voiceState === 'thinking' ? {
                                  transform: `scale(${1.2 + (processingPulse < 4 ? 4 - processingPulse : processingPulse - 4) * 0.05})`,
                                  opacity: 0.2 + (processingPulse < 4 ? 4 - processingPulse : processingPulse - 4) * 0.06,
                                  transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
                                } : {}}
                              />
                              <div
                                className="voice-blob-ring voice-blob-ring-3"
                                style={voiceState === 'thinking' ? {
                                  transform: `scale(${1.3 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.04})`,
                                  opacity: 0.15 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.05,
                                  transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
                                } : {}}
                              />
                            </div>
                          </div>
                        )}
                        <div className="voice-status-text">
                          {voiceState === 'initializing' && (t('voice-connecting') || 'Connecting...')}
                          {voiceState === 'listening' && (t('voice-listening') || 'Listening – speak now')}
                          {voiceState === 'thinking' && (t('voice-processing') || 'Processing...')}
                          {voiceState === 'speaking' && (t('voice-speaking') || 'Speaking...')}
                        </div>
                        {voiceState === 'initializing' && (
                          <div className="voice-hint-text">{t('voice-setting-up') || 'Setting up microphone...'}</div>
                        )}
                        {(voiceState === 'thinking' || voiceState === 'speaking') && (
                          <div className="voice-hint-text">{t('voice-tap-interrupt') || 'Tap to interrupt and speak again'}</div>
                        )}
                        <div className="voice-controls">
                          <button
                            className="voice-control-btn voice-cancel-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              stopVoice();
                            }}
                            aria-label="Close voice mode"
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='chat-input-wrapper'>
                    <div className="chat-input-container">
                      {isAudioLoading && <div className="audio-loader-expandable"></div>}
                      <textarea
                        ref={textAreaRef}
                        className="chat-textarea"
                        value={newMessage}
                        onChange={handleInput}
                        onKeyPress={handleKeyPress}
                        placeholder={isAudioLoading ? "" : t('ask_question')}
                        disabled={loading || isAudioLoading || voiceState !== 'idle'}
                        rows="1"
                      />
                      <button
                        onClick={handleSendMessage}
                        className="send-button"
                        disabled={loading || isAudioLoading || voiceState !== 'idle'}
                      >
                        <FontAwesomeIcon
                          icon={faCircleArrowUp}
                          className='send-icon'
                        />
                      </button>
                      <button
                        className="send-button"
                        onClick={handleRecordClick}
                        disabled={loading || isAudioLoading || voiceState !== 'idle'}
                      >
                          {isRecording ? (
                            <FontAwesomeIcon icon={faCircle} className='record-icon-recording' />
                          ) : (
                            <FontAwesomeIcon icon={faMicrophone} className='record-icon' />
                          )}
                      </button>
                      {/* Voice mode button */}
                      <button
                        onClick={toggleVoice}
                        disabled={loading || isAudioLoading}
                        className={`voice-button${voiceState !== 'idle' ? ' voice-active' : ''}`}
                        aria-label="AI Voice Agent"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 3V21M8 8V16M4 10V14M16 8V16M20 10V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <span className="voice-tooltip">{t('voice-agent') || 'AI Voice Agent'}</span>
                      </button>
                    </div>
                    <div className='clear-chat-button-container'>
                      <button
                        onClick={resetMessage}
                        className='clear-chat-button'
                        title={t('reset-chat')}
                        disabled={loading || isAudioLoading || voiceState !== 'idle'}
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          className='clear-chat-icon'
                        />
                      </button>
                    </div>
                  </div>
                </>
              )}
              {selectedTab === 'calendar' && (
                <div className="calendar-content">
                  <label
                    htmlFor="dateInput"
                    style={{
                      fontWeight: '600',
                      marginBottom: '5px',
                      display: 'block',
                      fontSize: '15px'
                    }}
                  >
                    {t('date')}
                  </label>
                  <input
                    type="date"
                    id="dateInput"
                    className='calendar-date-input'
                    value={selectedDate}
                    onChange={handleDateChange}
                  />
                  {calendarLoading && (
                    <div className="chat-bot-loader">
                      <div className="chat-loader"></div>
                      <span className="loading-text">{t('load-calendar')}</span>
                    </div>
                  )}
                  {events.length > 0 ? (
                    <div className='event-parent-container'>
                      {events.map((event, index) => {
                        const { time: startTime } = formatDateTime(event.start.dateTime);
                        const { time: endTime } = formatDateTime(event.end.dateTime);
                        return (
                          <div
                            className='event-parent'
                            key={index}
                            onClick={() => handleEventClick(event)}
                          >
                            <p className='event-index'>
                              {startTime} - {endTime} Uhr
                            </p>
                            <p className='event-title'>
                              {event.summary}
                            </p>
                            <div className='event-details-bottom'>
                              <div className='event-details-bottom-content'>
                                <p className='event-details-title'>
                                  {t('description')}
                                </p>
                                <p>{event.description}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    !calendarLoading && <p>{t('no-events')}</p>
                  )}
                </div>
              )}
              {selectedTab === 'vip' && (
                <div>
                  {vips.length > 0 ? (
                    <div className='vip-item-parent-container'>
                      {vips.map((vip, index) => (
                        <div className="vip-item" key={vip.id}>
                          <div
                            className='vip-item-content'
                            onClick={() => handleEventClickVip(vip.prompt)}
                          >
                            <div className='vip-item-header'>#{index + 1}</div>
                            <div>{vip.prompt}</div>
                          </div>
                          <button
                            onClick={() => handleDeleteVip(vip.id)}
                            className='delete-vip-button'
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              className='delete-vip-icon'
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>Keine gespeicherten Prompts</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ExpandableAssistantLion;
